import { PureQueryOptions } from '@apollo/client';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { useMutationErrorHandler } from '../../../../../../hooks/useMutationErrorHandler';
import { AutoBreaksRules } from '../../../../components/AutoBreaks/shared';

import TimeEntryForm from '../components/TimeEntryForm';
import {
  EmployeeType,
  isDirtyTimeEntryItem,
  TimeEntryItem,
  TimeEntryReadOnlyReason,
} from '../logic';
import {
  ProjectsDocument,
  TimeSheetDayProjectTimeEntryFragment,
  useSaveProjectTimeEntriesByDaysMutation,
} from '../../../../__generated__/graphql';
import {
  mapToSubmitTimeEntryInput,
  mapProjectTimeEntriesForTimeEntry,
} from '../logic/mapping';
import {
  getValidationErrorMessages,
  validateTimeEntry,
  validateTimeEntriesNotOverlap,
} from '../logic/validation';
import { getInitialState, timeEntryStateReducer } from './state';
import { getSubmitDisabledTitle, isDirtyProjectEntries } from './helpers';
import TimeEntryDumb from './TimeEntryDumb';
import { useTimeEntryManagersMutations } from './useTimeEntryManagersMutations';
import { TimeEntryManagerButtons } from './TimeEntryManagerButtons';
import {
  Props as RenderLayoutProps,
  TimeEntryCardLayout,
} from '../components/TimeEntryCardLayout';
import { CostCenterProps } from '..';
import { TimeTrackingErrorFragment } from '../../../../__generated__/graphql';
import { useTimeTrackingErrorsAndWarnings } from '../../../../hooks/timeTracking/useTimeTrackingErrorsAndWarnings';
import { useTimeTrackingContext } from '../../../../hooks/timeTrackingSettings/TimeTrackingContext';
import {
  ProjectWithStatus,
  TimeEntryProps,
  ProjectTimeEntry,
} from '../../../Projects/types';
import { useTimeTrackingRestriction } from '../../../../hooks/timeTrackingSettings';
import { useToasts } from '../../../../../../context/Toast';
import { i18prefix, timesheetsRange } from '../../../Projects/helpers';
import { toISODateOnly } from '../../../../../../utils/dateUtils';
import { notEmpty } from '../../../../../../utils/array';
import {
  mapToProjectDayTimeTracked,
  mapToTimeEntryInput,
} from '../../../Projects/ProjectsTimesheets/mappings';
import { UNIQUE_ID_PREFFIX } from '../../../Projects/ProjectsTimesheets/helpers';
import { minToHoursInput } from '../components/ProjectDayTimeEntriesForm/utils';
import { useAutoBreaksLogic } from '../logic/useAutoBreaksLogic';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

export type Props = {
  costCenters: readonly CostCenterProps[];
  autoBreakRules: AutoBreaksRules | null;
  value: TimeEntryItem;
  allTimeEntries: TimeEntryItem[];
  employeeType: EmployeeType;
  readonlyReasons: TimeEntryReadOnlyReason[];
  onCancelCreatingNewTimeEntry: () => void;
  // Why we have that method (problem): We have 2 parallel hierarchy of MyTimeSheet/TimeSheet MyTimeEntry/TimeEntry so it is hard to update cache for 2 branches of entries
  // The solution: Merge MyTimeSheet and TimeSheet and so on.. https://linear.app/flair/issue/FLA-2661/merge-timetracking-graphql-schema-mytimeentry-timeentry-and-so-on
  refetchQueriesForEmployeeMode?: PureQueryOptions[];
  // Why we have that method (problem): We use react-table expanding feature for expanding day rows.
  // useTimeSheetDetails makes direct call to apolloClient.query. It doesn't support reactive reloads.
  // The solution: Start use useQuery from apollo to allow auto update changed values.
  // But for this we need to implement custom rendering of react-table rows and custom expanding feature
  onChanged?: () => void;
  renderLayout?: (renderProps: RenderLayoutProps) => React.ReactNode;
  onAddNewTimeEntryClick: () => void;
  isLastEntry: boolean;
  projectTimeEntries?: ReadonlyArray<TimeSheetDayProjectTimeEntryFragment>;
  restrictedFutureEntries?: boolean;
  projects?: ProjectWithStatus[];
  onProjectTimeEntriesMinutesChange?: (
    timeEntryId: string | null,
    minutes: number,
  ) => void;
  timeEntriesForDay?: TimeEntryProps[];
  showTimeEntries?: boolean;
  isTimeTrackingWithProjects: boolean;
};

const TimeEntryManagerSmart: React.FC<Props> = ({
  costCenters,
  autoBreakRules,
  value,
  allTimeEntries,
  employeeType,
  refetchQueriesForEmployeeMode,
  readonlyReasons,
  onCancelCreatingNewTimeEntry,
  onChanged,
  renderLayout,
  onAddNewTimeEntryClick,
  isLastEntry,
  projectTimeEntries,
  restrictedFutureEntries,
  projects,
  onProjectTimeEntriesMinutesChange,
  timeEntriesForDay,
  showTimeEntries,
  isTimeTrackingWithProjects,
}) => {
  const { employeeId } = useTimeTrackingContext();

  const [state, dispatch] = useReducer(
    timeEntryStateReducer,
    getInitialState(value),
  );
  const { current, validationErrors, notesVisible, costCenterVisible } = state;

  const refetchQueries = [
    ...(refetchQueriesForEmployeeMode ?? []),
    {
      query: ProjectsDocument,
      variables: {
        from: moment(current.start.date)
          .startOf('day')
          .subtract(1, 'day')
          .toISOString(),
        to: moment(current.start.date).endOf('day').add(1, 'day').toISOString(),
      },
    },
  ];

  const { mutations, mutationsProgress } = useTimeEntryManagersMutations({
    changeRequestId: value.changeRequestId,
    /* eslint-disable-next-line no-restricted-syntax */
    refetchQueries,
    isNewTimeEntry: value.isNew,
  });

  const { showErrorsAndWarningsToasts } = useTimeTrackingErrorsAndWarnings();

  const readonly = readonlyReasons.length > 0;

  const { isSaving, isRejecting, isApproving, isDeleting } = mutationsProgress;

  const isInProgress = isSaving || isRejecting || isApproving || isDeleting;

  const fireOnChanged = () => onChanged && onChanged();

  const errorHandler = useMutationErrorHandler();

  const [currentProjectEntries, setCurrentProjectEntries] = useState<
    ProjectTimeEntry[]
  >([]);
  const initialProjectEntriesRef = useRef<ProjectTimeEntry[]>([]);
  const hasSetInitialRef = useRef(false);
  const [resetCounter, setResetCounter] = useState(0);

  const isTimeEntryDirty = isDirtyTimeEntryItem(value, current);
  const isProjectEntriesDirty =
    hasSetInitialRef.current &&
    isDirtyProjectEntries(
      currentProjectEntries,
      initialProjectEntriesRef.current,
    );
  const isDirty = isTimeEntryDirty || isProjectEntriesDirty;

  const isApproveMode = current.changeRequestId !== null;
  const readonlyIntervalToCompare = isApproveMode
    ? current.timeEntryOldInterval!
    : null;

  const isEditNotesAllowed = employeeType !== 'manager';

  const handleCancel = () => {
    if (value.isNew) {
      onCancelCreatingNewTimeEntry();
    } else {
      dispatch({ type: 'reset', initialTimeEntry: value });
      setResetCounter((prev) => prev + 1);
      hasSetInitialRef.current = false;
    }
  };

  const handleSubmit = () => {
    if (isApproveMode) {
      handleApprove();
    } else {
      handleSaveChanges();
    }
  };

  const handleSaveChanges = () => {
    const { current } = state;
    if (isInProgress) {
      return;
    }
    const errors = validateTimeEntry(current);

    if (errors.size !== 0) {
      return;
    }

    const updatedTimeEntries = allTimeEntries.map((entry) =>
      entry.uniqueId === current.uniqueId ? current : entry,
    );
    const overlapErrors = validateTimeEntriesNotOverlap(updatedTimeEntries);
    if (overlapErrors.size !== 0) {
      const allErrors = new Map([...state.validationErrors, ...overlapErrors]);
      dispatch({ type: 'setValidationErrors', errors: allErrors });
      return;
    }

    const projectEntriesToSave = currentProjectEntries
      .map((entry) =>
        mapToProjectDayTimeTracked(entry, moment(current.start.date)),
      )
      .filter(notEmpty)
      .map((mapped) => ({
        ...mapped,
        timesheetId: current.timeSheetId,
        dateId: toISODateOnly(new Date(mapped.date)),
      }));

    const hadProjectEntriesBefore = initialProjectEntriesRef.current.length > 0;
    const shouldIncludeProjectEntries =
      projectEntriesToSave.length > 0 || hadProjectEntriesBefore;
    const input = mapToSubmitTimeEntryInput(
      current,
      employeeId,
      shouldIncludeProjectEntries ? projectEntriesToSave : undefined,
    );
    mutations
      .saveChanges(input)
      .then((result) => {
        const errors: ReadonlyArray<TimeTrackingErrorFragment> =
          result.data?.timeTracking.submitTimeEntry.errors2 || [];
        showErrorsAndWarningsToasts({ errors });
        initialProjectEntriesRef.current = currentProjectEntries;
        onCancelCreatingNewTimeEntry();
        fireOnChanged();
      })
      .catch(errorHandler);
  };

  const handleApprove = () => {
    if (isInProgress || !current.changeRequestId) {
      return;
    }
    fireOnChanged();
    mutations.approve(current.changeRequestId, () => fireOnChanged());
  };

  const handleReject = () => {
    if (isInProgress || !current.changeRequestId) {
      return;
    }
    mutations
      .rejectChangeRequest(current.changeRequestId)
      .then(fireOnChanged)
      .catch(errorHandler);
  };

  const handleTimeEntryDelete = () => {
    if (isInProgress || !current.timeEntryId) {
      return;
    }
    mutations
      .deleteTimeEntry(current.timeEntryId)
      .catch(errorHandler)
      .then(fireOnChanged);
  };

  const {
    showRestrictedWarningModal,
    setShowRestrictedWarningModal,
    getTimeTrackingRestrictedReason,
  } = useTimeTrackingRestriction();
  const { addSuccess } = useToasts();
  const { t } = useTranslation();
  const [
    saveProjectTimeEntriesMutation,
    { loading: isSavingProjectTimeEntries },
  ] = useSaveProjectTimeEntriesByDaysMutation();

  const restrictionReason = getTimeTrackingRestrictedReason(current.start.date);

  const handleAddBreak = useCallback(() => {
    if (restrictionReason) {
      setShowRestrictedWarningModal(true);
    } else {
      dispatch({ type: 'newBreak', breakRules: autoBreakRules });
    }
  }, [
    restrictionReason,
    setShowRestrictedWarningModal,
    dispatch,
    autoBreakRules,
  ]);

  const mappedProjectTimeEntries = useMemo(
    () =>
      mapProjectTimeEntriesForTimeEntry(
        projectTimeEntries,
        current.timeEntryId,
      ),
    [projectTimeEntries, current.timeEntryId],
  );

  const totalProjectTimeEntriesMinutes = useMemo(() => {
    return mappedProjectTimeEntries.reduce(
      (total, entry) => total + (entry.minutes || 0),
      0,
    );
  }, [mappedProjectTimeEntries]);

  const [
    currentProjectTimeEntriesMinutes,
    setCurrentProjectTimeEntriesMinutes,
  ] = useState<number | null>(null);

  useEffect(() => {
    setCurrentProjectTimeEntriesMinutes(null);
    setCurrentProjectEntries(mappedProjectTimeEntries);
    initialProjectEntriesRef.current = mappedProjectTimeEntries;
    hasSetInitialRef.current = true;
  }, [mappedProjectTimeEntries, resetCounter]);

  const projectEntriesResetKey = useMemo(
    () => `${current.timeEntryId || 'new'}-${resetCounter}`,
    [current.timeEntryId, resetCounter],
  );

  const handleProjectTimeEntriesMinutesChangeInternal = useCallback(
    (minutes: number) => {
      setCurrentProjectTimeEntriesMinutes(minutes);
      onProjectTimeEntriesMinutesChange?.(current.timeEntryId, minutes);
    },
    [onProjectTimeEntriesMinutesChange, current.timeEntryId],
  );

  const handleSaveProjectTimeEntries = useCallback(
    (timesTracked: ProjectTimeEntry[]) => {
      const timeEntriesToSave = timesTracked
        .map((timeTracked) => {
          const mapped = mapToProjectDayTimeTracked(
            timeTracked,
            moment(current.start.date),
          );
          if (mapped && !mapped.timeEntryId && current.timeEntryId) {
            const isNewEntry =
              !timeTracked.id || timeTracked.id.includes(UNIQUE_ID_PREFFIX);
            if (isNewEntry) {
              return {
                ...mapped,
                timeEntryId: current.timeEntryId,
              };
            }
          }
          return mapped;
        })
        .filter(notEmpty);

      return saveProjectTimeEntriesMutation({
        variables: {
          input: {
            date: toISODateOnly(new Date(current.start.date)),
            timesheetId: current.timeSheetId,
            timeEntries: timeEntriesToSave.map(mapToTimeEntryInput),
          },
          ...timesheetsRange(),
        },
      })
        .then(() => {
          setCurrentProjectTimeEntriesMinutes(null);
          initialProjectEntriesRef.current = timesTracked;
          addSuccess(t(`${i18prefix}.projectTimeEntries.saveSuccess`));
        })
        .catch(errorHandler);
    },
    [
      current.start.date,
      current.timeSheetId,
      current.timeEntryId,
      saveProjectTimeEntriesMutation,
      addSuccess,
      t,
      errorHandler,
    ],
  );

  const [showProjectTimeEntriesForm, setShowProjectTimeEntriesForm] =
    useState<boolean>(false);

  const hasProjectTimeEntries = mappedProjectTimeEntries.length > 0;

  useEffect(() => {
    if (hasProjectTimeEntries) {
      setShowProjectTimeEntriesForm(true);
    }
  }, [hasProjectTimeEntries]);

  const canShowProjectTimeEntries =
    projects &&
    (hasProjectTimeEntries || showProjectTimeEntriesForm) &&
    handleSaveProjectTimeEntries;

  const showConnectToProjectsButton =
    isTimeTrackingWithProjects &&
    !readonly &&
    !isApproveMode &&
    projects &&
    projects.length > 0 &&
    !hasProjectTimeEntries &&
    !showProjectTimeEntriesForm;

  const handleConnectToProjects = useCallback(() => {
    setShowProjectTimeEntriesForm(true);
  }, []);

  const handleCancelProjectTimeEntries = useCallback(() => {
    if (!hasProjectTimeEntries) {
      setShowProjectTimeEntriesForm(false);
    }
  }, [hasProjectTimeEntries]);

  const { timeEntryStats } = useAutoBreaksLogic({
    timeEntry: current,
    autoBreakRules,
    validationErrors,
  });

  const initialHours = useMemo(() => {
    if (!hasProjectTimeEntries && showProjectTimeEntriesForm) {
      return minToHoursInput(timeEntryStats.workingMinutes);
    }
    return undefined;
  }, [
    hasProjectTimeEntries,
    showProjectTimeEntriesForm,
    timeEntryStats.workingMinutes,
  ]);

  const newTimeEntry = current.timeEntryId === null;

  const getButtonsMode = () => {
    if (!isApproveMode && !readonly && isDirty) {
      return 'submit';
    }
    if (isApproveMode) {
      return 'approve';
    }
    return null;
  };

  const buttonsMode = getButtonsMode();

  const validationMessages = getValidationErrorMessages(validationErrors);
  const submitDisabledTitle = getSubmitDisabledTitle(
    validationMessages,
    isInProgress,
    t,
  );

  const timeEntryButtons =
    buttonsMode !== null ? (
      <TimeEntryManagerButtons
        mode={buttonsMode}
        onCancel={handleCancel}
        onReject={handleReject}
        isInProgress={isInProgress}
        isApproving={isApproving}
        isSaving={isSaving}
        isRejecting={isRejecting}
        submitDisabled={isInProgress || validationErrors.size > 0}
        submitDisabledTitle={submitDisabledTitle}
      />
    ) : null;

  const timeEntryContent = (
    <TimeEntryDumb
      autoBreakRules={autoBreakRules}
      timeEntry={current}
      costCenters={costCenters}
      validationErrors={validationErrors}
      readonly={readonly || isApproveMode}
      readonlyReasons={readonlyReasons}
      readonlyIntervalToCompare={readonlyIntervalToCompare || undefined}
      notesVisible={notesVisible}
      costCenterVisible={costCenterVisible}
      addNotesButtonVisible={!notesVisible}
      addAssignCostCenterButtonVisible={
        costCenters.length > 0 && !costCenterVisible
      }
      isDeleteBreakAllowed={!isApproveMode}
      isEditNotesAllowed={isEditNotesAllowed}
      isDeleteTimeEntryAllowed={!readonly && !isApproveMode && !newTimeEntry}
      isTimeEntryDeleting={isDeleting}
      forceShowEndDate={true}
      onTimeEntryDelete={handleTimeEntryDelete}
      dispatch={dispatch}
      onAddNewTimeEntryClick={onAddNewTimeEntryClick}
      newTimeEntry={newTimeEntry}
      isLastEntry={isLastEntry}
      restrictedFutureEntries={restrictedFutureEntries}
      projects={projects}
      timeEntriesForDay={timeEntriesForDay}
      showTimeEntries={showTimeEntries}
      isTimeTrackingWithProjects={isTimeTrackingWithProjects}
      restrictionReason={restrictionReason}
      showRestrictedWarningModal={showRestrictedWarningModal}
      onShowRestrictedWarningModal={setShowRestrictedWarningModal}
      onAddBreak={handleAddBreak}
      mappedProjectTimeEntries={mappedProjectTimeEntries}
      totalProjectTimeEntriesMinutes={totalProjectTimeEntriesMinutes}
      currentProjectTimeEntriesMinutes={currentProjectTimeEntriesMinutes}
      onProjectTimeEntriesMinutesChangeInternal={
        handleProjectTimeEntriesMinutesChangeInternal
      }
      onSaveProjectTimeEntries={handleSaveProjectTimeEntries}
      isSavingProjectTimeEntries={isSavingProjectTimeEntries}
      showProjectTimeEntriesForm={showProjectTimeEntriesForm}
      onShowProjectTimeEntriesForm={setShowProjectTimeEntriesForm}
      onConnectToProjects={handleConnectToProjects}
      onCancelProjectTimeEntries={handleCancelProjectTimeEntries}
      canShowProjectTimeEntries={!!canShowProjectTimeEntries}
      showConnectToProjectsButton={!!showConnectToProjectsButton}
      timeEntryStats={timeEntryStats}
      initialHours={initialHours}
      onProjectEntriesChange={setCurrentProjectEntries}
      projectEntriesResetKey={projectEntriesResetKey}
    />
  );

  const renderLayoutProps = {
    isProcessing: isInProgress,
    highlight: isDirty,
    buttons: timeEntryButtons,
    children: timeEntryContent,
  };

  return (
    <TimeEntryForm onSubmit={handleSubmit} onCancel={handleCancel}>
      {renderLayout ? (
        renderLayout(renderLayoutProps)
      ) : (
        <TimeEntryCardLayout {...renderLayoutProps} />
      )}
    </TimeEntryForm>
  );
};

export default TimeEntryManagerSmart;
